import { GameSection } from './GameSection';
import { cn } from '@/lib/utils';
import { GameIcon } from '@/utils/iconRenderer';
import type { Game } from '@/types/game';

interface ComingSoonGameCardProps {
  game: Game;
  onClick: () => void;
}

function ComingSoonGameCard({ game, onClick }: ComingSoonGameCardProps) {
  // Map sport to Tailwind color classes
  // Dark mode uses darker backgrounds with lighter text for contrast
  const sportColorClasses = {
    football: {
      bg: 'bg-sport-football-secondary dark:bg-sport-football-dark',
      icon: 'bg-sport-football-primary',
      title: 'text-sport-football-dark dark:text-sport-football-light',
      tagline: 'text-sport-football-primary dark:text-sport-football-secondary',
      badge: 'bg-sport-football-light dark:bg-sport-football-primary text-sport-football-primary dark:text-sport-football-light',
    },
    basketball: {
      bg: 'bg-sport-basketball-secondary dark:bg-sport-basketball-dark',
      icon: 'bg-sport-basketball-primary',
      title: 'text-sport-basketball-dark dark:text-sport-basketball-light',
      tagline: 'text-sport-basketball-primary dark:text-sport-basketball-secondary',
      badge: 'bg-sport-basketball-light dark:bg-sport-basketball-primary text-sport-basketball-primary dark:text-sport-basketball-light',
    },
    baseball: {
      bg: 'bg-sport-baseball-secondary dark:bg-sport-baseball-dark',
      icon: 'bg-sport-baseball-primary',
      title: 'text-sport-baseball-dark dark:text-sport-baseball-light',
      tagline: 'text-sport-baseball-primary dark:text-sport-baseball-secondary',
      badge: 'bg-sport-baseball-light dark:bg-sport-baseball-primary text-sport-baseball-primary dark:text-sport-baseball-light',
    },
  };

  const colors = sportColorClasses[game.sport as keyof typeof sportColorClasses] || sportColorClasses.baseball;

  return (
    <div
      className={cn(
        "rounded-xl p-3 md:p-[clamp(10px,1.2vw,14px)] cursor-pointer transition-all hover:shadow-md",
        colors.bg
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-[clamp(12px,3vw,16px)] md:gap-[clamp(8px,1vw,14px)]">
        {/* Game Icon - using GameIcon utility for consistent rendering */}
        <div className="flex-shrink-0">
          <GameIcon
            iconUrl={game.iconUrl}
            backgroundColor={game.signatureColor}
            sizeClassName="w-[clamp(72px,19vw,96px)] h-[clamp(72px,19vw,96px)] md:w-[clamp(90px,12vw,115px)] md:h-[clamp(90px,12vw,115px)] xl:w-[clamp(56px,8vw,88px)] xl:h-[clamp(56px,8vw,88px)]"
            alt={game.displayName}
          />
        </div>

        {/* Title and Tagline - matches GameCard sizing across all breakpoints */}
        <div className="flex-1 min-w-0">
          <h3
            className={cn(
              "font-bebas-neue text-[clamp(16px,4vw,24px)] md:text-[clamp(18px,2.2vw,30px)] xl:text-[clamp(16px,1.4vw,24px)] font-medium tracking-[1.5px] uppercase leading-none",
              colors.title
            )}
          >
            {game.displayName}
          </h3>
          <p
            className={cn(
              "font-oswald text-[clamp(8px,2vw,12px)] md:text-[clamp(10px,1vw,14px)] xl:text-[clamp(9px,0.65vw,12px)] font-medium uppercase tracking-[0.5px] mt-2 md:mt-4 xl:mt-2.5 leading-tight",
              colors.tagline
            )}
          >
            {game.tagline}
          </p>
        </div>

        {/* Coming Soon Badge */}
        <div className="flex-shrink-0">
          <div
            className={cn(
              "rounded px-[clamp(12px,3vw,18px)] md:px-[clamp(12px,1vw,20px)] py-[clamp(6px,1.5vw,10px)] md:py-[clamp(6px,0.5vw,10px)] font-oswald text-[clamp(11px,2.8vw,14px)] md:text-[clamp(11px,0.8vw,14px)] font-bold uppercase tracking-[0.5px] whitespace-nowrap",
              colors.badge
            )}
          >
            Coming Soon
          </div>
        </div>
      </div>
    </div>
  );
}

export interface ComingSoonSectionProps {
  className?: string;
}

export function ComingSoonSection({ className }: ComingSoonSectionProps) {
  // Hard-coded Basketball and Football games
  const comingSoonGames: Game[] = [
    {
      id: 'game_basketball_daily',
      displayName: 'Daily Basketball Challenges',
      tagline: 'NBA puzzles',
      iconUrl: '/logos/sport-basketball.svg',
      signatureColor: '#FB8320', // Orange for Basketball (matches icon)
      challengeType: 'daily',
      gameFrequency: 'daily',
      sport: 'basketball',
      isActive: false, // Not active yet
      isPublished: false, // Not published yet
      requiresPremium: false,
      gameType: 'internal',
      route: '/basketball',
      shortDescription: 'Test your NBA knowledge with daily lineup challenges',
      longDescription: 'Coming soon: Daily basketball challenges featuring NBA lineups, player combinations, and team strategies.',
      metadata: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    {
      id: 'game_football_daily',
      displayName: 'Daily Football Challenges',
      tagline: 'NFL puzzles',
      iconUrl: '/logos/sport-football.svg',
      signatureColor: '#4C7A32', // Green for Football (matches icon)
      challengeType: 'daily', 
      gameFrequency: 'daily',
      sport: 'football',
      isActive: false, // Not active yet
      isPublished: false, // Not published yet
      requiresPremium: false,
      gameType: 'internal',
      route: '/football',
      shortDescription: 'Test your NFL knowledge with daily lineup challenges',
      longDescription: 'Coming soon: Daily football challenges featuring NFL lineups, player combinations, and team strategies.',
      metadata: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
  ];

  const handleComingSoonClick = (gameName: string) => {
    // Simple alert for coming soon functionality
    alert(`${gameName} will be available soon! Stay tuned for updates.`);
  };

  return (
    <GameSection 
      title="Coming Soon" 
      className={className}
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {comingSoonGames.map(game => (
          <ComingSoonGameCard
            key={game.id}
            game={game}
            onClick={() => handleComingSoonClick(game.displayName)}
          />
        ))}
      </div>
    </GameSection>
  );
}